import { useEffect, useRef, useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { Activity, ArrowLeft, BarChart3, Loader2, Radar, RefreshCw, Save, Trash2, X } from 'lucide-react'
import { algorithmicScannerService } from '@/services/algorithmicScanner'
import { chartingService } from '@/services/charting'
import LoadingSpinner from '@/components/Common/LoadingSpinner'
import ToggleField from '@/components/Indicators/ToggleField'
import SignalPerformanceDashboard from '@/components/Scanner/SignalPerformanceDashboard'
import { useOutsideClick } from '@/hooks/useOutsideClick'
import ScannerMonteCarloDashboard from '@/components/Scanner/ScannerMonteCarloDashboard'
import { log as debugLog, error as debugError } from '@/utils/logger'

const TIMEFRAMES = ['15m', '30m', '1h', '2h', '4h', '6h', '8h', '12h', '1d', '3d', '1w']

const DEFAULT_CONFIG = {
  assets: [],
  timeframes: ['15m', '1h'],
  indicators: [],
  send_alert: true,
  ema50_filter: true,
  telegram_chat_id: '',
  telegram_thread_id: '',
}

const INDICATOR_DEFAULTS = {
  ict: { pivot_len: 5, atr_mult: 0.3, atr_len: 14, entry_mode: 'ob_or_fvg' },
  showi_ict: { pivotLen: 5, atrMult: 1.5, atrLen: 14, tp1RR: 1.0, tp2RR: 2.0, tp3RR: 3.0, slBufferATR: 1.5 },
  squeeze_breakout: {
    squeezeLength: 20, bbMult: 2.0, squeezeThresh: 1.8, minSqueezeBars: 4,
    impulseMult: 0.6, signalCooldown: 5, slBuffer: 0.5, tp1RR: 1.0, tp2RR: 2.0, tp3RR: 3.0,
    useVolFilter: false, volMult: 1.5,
  },
  smart_breakout: {
    squeezeLength: 20, bbMult: 2.0, squeezeThresh: 0.6, atrCompressRatio: 0.75, minSqueezeBars: 5,
    impulseMult: 0.8, slBuffer: 0.5, tp1RR: 1.0, tp2RR: 2.0, tp3RR: 3.0,
    useVolFilter: false, volMult: 1.5,
    useHtfFilter: false, htfTimeframe: '1d', htfMaLen: 50,
  },
  breakout_pattern: {
    pivotLen: 5, minTouches: 2, maxChannelBars: 120, convergenceMin: 0.02,
    volConfirm: 1.2, volContraction: false, momentumFilter: true,
    slPadding: 0.5, labelOffset: 20,
    touchTolerance: 0.15, deviationMax: 0.3, minChannelWidthAtr: 0.5,
  },
  canales_sr: {
    pivotLen: 21, atrLen: 14, minChannelBars: 10, maxChannelBars: 400,
    channelQuality: 0.55, showUp: true, showDown: true,
    alertBreak: true, alertBreakout: true, alertReact: true,
  },
  liquidity_sweep_pro: {
    swingLength: 21, maxSweepDistance: 80, minSweepScore: 50,
    requireChoch: true, structurePivotLength: 8, confirmationWindow: 13,
    useVolumeFilter: true, volumeMaLength: 21, volumeSpikeMult: 1.5,
    useHtfBias: true, htfTimeframe: '240', htfEmaLength: 50,
    riskPreset: 'Balanced', atrLength: 14, slBufferAtr: 0.25,
    tp1Mult: 1.0, tp2Mult: 2.0, tp3Mult: 3.0, breakEvenAfterTp1: true,
  },
  supertrend_quantum: {
    consensus_threshold: 0.60, base_index: 3, cloud_length: 8,
    sl_type: 'cluster_line', tp_type: 'rr', tp_rr: 2.0, atr_sl_mult: 2.0, atr_len: 14,
  },
}

const INDICATOR_META = {
  ict: { label: 'ICT / SMC', fields: [
    { key: 'pivot_len', label: 'Pivot len', type: 'int', min: 1, max: 20 },
    { key: 'atr_mult', label: 'ATR mult', type: 'float', min: 0.1, max: 5, step: 0.1 },
    { key: 'atr_len', label: 'ATR len', type: 'int', min: 5, max: 50 },
    { key: 'entry_mode', label: 'Entry mode', type: 'select', options: ['ob_or_fvg', 'ob', 'fvg'] },
  ]},
  showi_ict: { label: 'SHOWI ICT', fields: [
    { key: 'pivotLen', label: 'Pivot len', type: 'int', min: 2, max: 10 },
    { key: 'atrMult', label: 'ATR mult', type: 'float', min: 0.1, max: 5, step: 0.1 },
    { key: 'atrLen', label: 'ATR len', type: 'int', min: 5, max: 50 },
    { key: 'slBufferATR', label: 'SL buffer ATR', type: 'float', min: 0.1, max: 5, step: 0.1 },
    { key: 'tp1RR', label: 'TP1 R:R', type: 'float', min: 0.1, max: 10, step: 0.1 },
    { key: 'tp2RR', label: 'TP2 R:R', type: 'float', min: 0.1, max: 10, step: 0.1 },
    { key: 'tp3RR', label: 'TP3 R:R', type: 'float', min: 0.1, max: 10, step: 0.1 },
  ]},
  squeeze_breakout: { label: 'Squeeze Breakout', fields: [
    { key: 'squeezeLength', label: 'Squeeze length', type: 'int', min: 5, max: 50 },
    { key: 'bbMult', label: 'BB mult', type: 'float', min: 0.5, max: 4, step: 0.1 },
    { key: 'squeezeThresh', label: 'Squeeze thresh', type: 'float', min: 0.5, max: 5, step: 0.1 },
    { key: 'minSqueezeBars', label: 'Min squeeze bars', type: 'int', min: 2, max: 20 },
    { key: 'impulseMult', label: 'Impulse mult', type: 'float', min: 0.1, max: 2, step: 0.1 },
    { key: 'signalCooldown', label: 'Cooldown', type: 'int', min: 0, max: 50 },
    { key: 'slBuffer', label: 'SL buffer', type: 'float', min: 0.1, max: 3, step: 0.1 },
    { key: 'tp1RR', label: 'TP1 R:R', type: 'float', min: 0.1, max: 10, step: 0.1 },
    { key: 'tp2RR', label: 'TP2 R:R', type: 'float', min: 0.1, max: 10, step: 0.1 },
    { key: 'tp3RR', label: 'TP3 R:R', type: 'float', min: 0.1, max: 10, step: 0.1 },
  ]},
  smart_breakout: { label: 'Smart Breakout Targets', fields: [
    { key: 'squeezeLength', label: 'Squeeze length', type: 'int', min: 10, max: 100 },
    { key: 'bbMult', label: 'BB mult', type: 'float', min: 1, max: 4, step: 0.1 },
    { key: 'squeezeThresh', label: 'Umbral BB Width', type: 'float', min: 0.1, max: 1, step: 0.05 },
    { key: 'atrCompressRatio', label: 'Ratio compresión ATR', type: 'float', min: 0.3, max: 1, step: 0.05 },
    { key: 'minSqueezeBars', label: 'Min squeeze bars', type: 'int', min: 2, max: 50 },
    { key: 'impulseMult', label: 'Impulse mult', type: 'float', min: 0.2, max: 3, step: 0.1 },
    { key: 'slBuffer', label: 'SL buffer', type: 'float', min: 0.1, max: 3, step: 0.1 },
    { key: 'tp1RR', label: 'TP1 R:R', type: 'float', min: 0.1, max: 10, step: 0.1 },
    { key: 'tp2RR', label: 'TP2 R:R', type: 'float', min: 0.1, max: 10, step: 0.1 },
    { key: 'tp3RR', label: 'TP3 R:R', type: 'float', min: 0.1, max: 10, step: 0.1 },
  ]},
  breakout_pattern: { label: 'Patrón de Ruptura', fields: [
    { key: 'pivotLen', label: 'Pivot len', type: 'int', min: 2, max: 30 },
    { key: 'minTouches', label: 'Min touches', type: 'int', min: 2, max: 6 },
    { key: 'maxChannelBars', label: 'Max channel bars', type: 'int', min: 20, max: 500 },
    { key: 'convergenceMin', label: 'Convergence min', type: 'float', min: 0, max: 0.5, step: 0.01 },
    { key: 'volConfirm', label: 'Vol confirm', type: 'float', min: 0.5, max: 3, step: 0.1 },
    { key: 'slPadding', label: 'SL padding', type: 'float', min: 0, max: 1, step: 0.1 },
    { key: 'touchTolerance', label: 'Touch tolerance', type: 'float', min: 0.01, max: 0.5, step: 0.01 },
    { key: 'deviationMax', label: 'Deviation max', type: 'float', min: 0.05, max: 1, step: 0.05 },
    { key: 'minChannelWidthAtr', label: 'Min width (× ATR)', type: 'float', min: 0.1, max: 3, step: 0.1 },
    { key: 'volContraction', label: 'Vol contraction', type: 'bool' },
    { key: 'momentumFilter', label: 'Momentum filter', type: 'bool' },
  ]},
  canales_sr: { label: 'Canales S/R', fields: [
    { key: 'pivotLen', label: 'Pivot len', type: 'int', min: 2, max: 50 },
    { key: 'atrLen', label: 'ATR len', type: 'int', min: 5, max: 100 },
    { key: 'minChannelBars', label: 'Min channel bars', type: 'int', min: 3, max: 200 },
    { key: 'maxChannelBars', label: 'Max channel bars', type: 'int', min: 30, max: 2000 },
    { key: 'channelQuality', label: 'Channel quality', type: 'float', min: 0.2, max: 1, step: 0.01 },
    { key: 'showUp', label: 'Canales ascendentes', type: 'bool' },
    { key: 'showDown', label: 'Canales descendentes', type: 'bool' },
    { key: 'alertBreak', label: 'Alerta Buy/Sell', type: 'bool' },
    { key: 'alertBreakout', label: 'Alerta breakout', type: 'bool' },
    { key: 'alertReact', label: 'Alerta reacción', type: 'bool' },
  ]},
  liquidity_sweep_pro: { label: 'Liquidity Sweep Pro', fields: [
    { key: 'swingLength', label: 'Swing length', type: 'int', min: 3, max: 100 },
    { key: 'maxSweepDistance', label: 'Max sweep distance', type: 'int', min: 5, max: 500 },
    { key: 'minSweepScore', label: 'Min sweep score', type: 'int', min: 0, max: 100 },
    { key: 'requireChoch', label: 'Require CHoCH', type: 'bool' },
    { key: 'structurePivotLength', label: 'Structure pivot length', type: 'int', min: 2, max: 30 },
    { key: 'confirmationWindow', label: 'Confirmation window', type: 'int', min: 1, max: 100 },
    { key: 'useVolumeFilter', label: 'Use volume filter', type: 'bool' },
    { key: 'volumeMaLength', label: 'Volume MA length', type: 'int', min: 2, max: 200 },
    { key: 'volumeSpikeMult', label: 'Volume spike mult', type: 'float', min: 1.1, max: 5, step: 0.1 },
    { key: 'useHtfBias', label: 'Use HTF bias', type: 'bool' },
    { key: 'htfTimeframe', label: 'HTF timeframe', type: 'text' },
    { key: 'htfEmaLength', label: 'HTF EMA length', type: 'int', min: 5, max: 400 },
    { key: 'riskPreset', label: 'Risk preset', type: 'select', options: ['Conservative', 'Balanced', 'Aggressive', 'Scalping', 'Custom'] },
    { key: 'atrLength', label: 'ATR length', type: 'int', min: 5, max: 50 },
    { key: 'breakEvenAfterTp1', label: 'Break-even after TP1', type: 'bool' },
  ]},
  supertrend_quantum: { label: 'SuperTrend Quantum', fields: [
    { key: 'consensus_threshold', label: 'Umbral consenso', type: 'float', min: 0.2, max: 1, step: 0.05 },
    { key: 'base_index', label: 'Base index', type: 'int', min: 1, max: 5 },
    { key: 'cloud_length', label: 'Cloud length', type: 'int', min: 2, max: 50 },
    { key: 'sl_type', label: 'SL type', type: 'select', options: ['cluster_line', 'base_line', 'atr'] },
    { key: 'tp_type', label: 'TP type', type: 'select', options: ['rr', 'trailing', 'fixed'] },
    { key: 'tp_rr', label: 'TP R:R', type: 'float', min: 0.5, max: 10, step: 0.1 },
    { key: 'atr_sl_mult', label: 'ATR SL mult', type: 'float', min: 0.5, max: 10, step: 0.1 },
    { key: 'atr_len', label: 'ATR len', type: 'int', min: 5, max: 50 },
  ]},
}

const SOURCES = [
  { value: 'bingx', label: 'BingX' },
  { value: 'bitunix', label: 'Bitunix' },
  { value: 'twelve', label: 'TwelveData' },
]

const TWELVE_TYPES = [
  { value: 'all', label: 'Todos' },
  { value: 'stocks', label: 'Stocks' },
  { value: 'forex', label: 'Forex' },
  { value: 'indices', label: 'Índices' },
  { value: 'commodities', label: 'Commodities' },
  { value: 'etf', label: 'ETF' },
]

function AssetSymbolInput({ value, source, twelveType, onChange, disabled }) {
  const [query, setQuery] = useState(value || '')
  const [options, setOptions] = useState([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => { setQuery(value || '') }, [value])

  useOutsideClick(ref, () => setOpen(false))

  const fetchSymbols = async (q) => {
    if (!q || q.length < 1) {
      setOptions([])
      return
    }
    setLoading(true)
    try {
      let res
      if (source === 'twelve') {
        res = await chartingService.searchSymbols(q, 'twelve', twelveType || 'all')
      } else {
        res = await chartingService.searchSymbols(q, source, 'crypto')
      }
      setOptions((res.data || []).slice(0, 50))
    } catch {
      setOptions([])
    } finally {
      setLoading(false)
    }
  }

  const debounceRef = useRef(null)
  const handleInputChange = (e) => {
    const v = e.target.value.toUpperCase()
    setQuery(v)
    onChange(v)
    setOpen(true)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => fetchSymbols(v), 250)
  }

  const handleSelect = (symbol) => {
    setQuery(symbol)
    onChange(symbol)
    setOpen(false)
  }

  const placeholder = source === 'twelve' ? 'AAPL, EUR/USD…' : 'BTC/USDT…'

  return (
    <div ref={ref} className="relative flex-1">
      <input
        type="text"
        value={query}
        onChange={handleInputChange}
        onFocus={() => { if (query) setOpen(true) }}
        placeholder={placeholder}
        disabled={disabled}
        className="input pr-8"
      />
      {loading && <Loader2 size={14} className="animate-spin absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 dark:text-gray-500" />}
      {open && options.length > 0 && (
        <ul className="absolute z-50 mt-1 w-full max-h-56 overflow-y-auto bg-white dark:bg-gray-900 border border-slate-300 dark:border-gray-700 rounded-lg shadow-xl">
          {options.map((opt) => (
            <li
              key={opt.symbol}
              onMouseDown={() => handleSelect(opt.symbol)}
              className="px-3 py-2 text-sm cursor-pointer hover:bg-slate-100 dark:hover:bg-gray-800 text-slate-900 dark:text-gray-200"
            >
              <span className="font-mono font-medium">{opt.symbol}</span>
              {opt.description && <span className="ml-2 text-xs text-slate-500 dark:text-gray-400">{opt.description}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function Section({ title, children }) {
  return (
    <div className="card space-y-4">
      <h3 className="section-title">{title}</h3>
      {children}
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs text-slate-500 dark:text-gray-400 mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-slate-500 dark:text-gray-500 mt-1">{hint}</p>}
    </div>
  )
}

function NumberInput({ value, onChange, min, max, step = 1, disabled = false }) {
  return (
    <input
      type="number"
      min={min}
      max={max}
      step={step}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === '' ? '' : parseFloat(e.target.value))}
      className="input"
    />
  )
}

function mergeDefaults(config) {
  return {
    ...DEFAULT_CONFIG,
    ...config,
    indicators: (config?.indicators || []).map((ind) => ({
      ...ind,
      config: { ...(INDICATOR_DEFAULTS[ind.id] || {}), ...(ind.config || {}) },
    })),
  }
}

function formatError(err, fallback = 'Error inesperado') {
  if (!err) return fallback
  if (typeof err === 'string') return err
  if (Array.isArray(err)) {
    return err.map((e) => formatError(e)).join(' · ')
  }
  if (typeof err === 'object') {
    if (err.msg) return err.msg
    if (err.message) return err.message
    if (err.detail) return formatError(err.detail, fallback)
    return JSON.stringify(err)
  }
  return String(err)
}

export default function AlgorithmicScannerDetailPage() {
  const { scannerId } = useParams()
  const navigate = useNavigate()
  const [scanner, setScanner] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [diagnosis, setDiagnosis] = useState(null)
  const [diagnosing, setDiagnosing] = useState(false)
  const [mcOpen, setMcOpen] = useState(false)
  const [mcParams, setMcParams] = useState({ lookback_days: 180, n_simulations: 5000 })
  const [mcSelectedAssets, setMcSelectedAssets] = useState([])
  const [mcSelectedTimeframes, setMcSelectedTimeframes] = useState([])
  const [mcLoading, setMcLoading] = useState(false)
  const [mcResult, setMcResult] = useState(null)
  const [mcError, setMcError] = useState(null)
  const [mcJobs, setMcJobs] = useState([])
  const mcTimersRef = useRef(new Map())
  const mcBackoffRef = useRef(new Map())
  const MC_JOBS_KEY = `quantum:mc_jobs:${scannerId}`
  const MAX_STORED_JOBS = 50
  const FAILED_JOB_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000
  const [error, setError] = useState(null)
  const [tab, setTab] = useState(0)
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  useEffect(() => {
    loadConfig()
  }, [scannerId])

  // Cargar historial de jobs Monte Carlo desde localStorage.
  // Descartamos jobs fallidos antiguos (>7 días) para evitar que el historial
  // se llene de errores obsoletos, pero conservamos los completados.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(MC_JOBS_KEY)
      if (raw) {
        const parsed = JSON.parse(raw)
        if (Array.isArray(parsed)) {
          const now = Date.now()
          const cleaned = parsed.filter((job) => {
            if (job.status === 'failed' || job.error) {
              const ts = job.finishedAt || job.createdAt
              if (ts && now - new Date(ts).getTime() > FAILED_JOB_MAX_AGE_MS) {
                return false
              }
            }
            return true
          })
          setMcJobs(cleaned.slice(-MAX_STORED_JOBS))
        }
      }
    } catch (e) {
      debugError('[MC] Error cargando historial de localStorage:', e)
    }
  }, [MC_JOBS_KEY])

  // Persistir historial de jobs Monte Carlo en localStorage
  useEffect(() => {
    try {
      localStorage.setItem(MC_JOBS_KEY, JSON.stringify(mcJobs.slice(-MAX_STORED_JOBS)))
    } catch (e) {
      debugError('[MC] Error guardando historial en localStorage:', e)
    }
  }, [mcJobs, MC_JOBS_KEY])

  // Polling en segundo plano de análisis Monte Carlo
  // - Intervalo base 10 s con backoff exponencial hasta 60 s si el servidor devuelve 429.
  // - No se actualiza el estado en cada 429 para evitar re-renders y tormentas de requests.
  // - Los timers y el backoff se mantienen estables entre renders; solo se cancelan al desmontar.
  useEffect(() => {
    const BASE_DELAY = 10000
    const MAX_DELAY = 60000
    let cancelled = false

    const getActiveIds = () =>
      new Set(mcJobs.filter((j) => j.status === 'running' || j.status === 'queued').map((j) => j.resultId))

    const schedulePoll = (resultId, delay) => {
      if (mcTimersRef.current.has(resultId)) return
      const timer = setTimeout(() => runPoll(resultId), delay)
      mcTimersRef.current.set(resultId, timer)
    }

    const runPoll = async (resultId) => {
      mcTimersRef.current.delete(resultId)
      if (cancelled) return
      // Si el job dejó de estar activo, no continuamos
      if (!getActiveIds().has(resultId)) return
      try {
        const { data } = await algorithmicScannerService.getMonteCarloResult(resultId)
        if (cancelled) return
        // Éxito: resetear backoff
        mcBackoffRef.current.set(resultId, BASE_DELAY)
        if (data.status === 'completed' || data.status === 'failed') {
          setMcJobs((prev) =>
            prev.map((job) =>
              job.resultId === resultId
                ? {
                    ...job,
                    status: data.status,
                    result: data.status === 'completed' ? data : null,
                    error: data.error || null,
                    finishedAt: new Date().toISOString(),
                  }
                : job
            )
          )
          mcBackoffRef.current.delete(resultId)
          return
        }
      } catch (err) {
        if (cancelled) return
        const is429 = err.response?.status === 429
        const currentDelay = mcBackoffRef.current.get(resultId) || BASE_DELAY
        const nextDelay = Math.min(currentDelay * (is429 ? 2 : 1.5), MAX_DELAY)
        mcBackoffRef.current.set(resultId, nextDelay)
        if (!is429) {
          // Error no recuperable: marcar como fallado y no reintentar
          setMcJobs((prev) =>
            prev.map((job) =>
              job.resultId === resultId
                ? { ...job, status: 'failed', error: formatError(err.response?.data?.detail, 'Error de polling') }
                : job
            )
          )
          mcBackoffRef.current.delete(resultId)
          return
        }
        // 429: no tocamos mcJobs para evitar re-render; el backoff ya está guardado
      }
      if (cancelled) return
      schedulePoll(resultId, mcBackoffRef.current.get(resultId) || BASE_DELAY)
    }

    // Programar polls para jobs activos que aún no tienen timer
    getActiveIds().forEach((id) => {
      if (!mcTimersRef.current.has(id)) {
        schedulePoll(id, mcBackoffRef.current.get(id) || BASE_DELAY)
      }
    })

    return () => {
      cancelled = true
      mcTimersRef.current.forEach(clearTimeout)
      mcTimersRef.current.clear()
      mcBackoffRef.current.clear()
    }
  }, [mcJobs])

  const loadConfig = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await algorithmicScannerService.getScanner(scannerId)
      setScanner({
        ...data,
        config: mergeDefaults(data.config),
      })
      setHasUnsavedChanges(false)
    } catch (e) {
      setError(formatError(e.response?.data?.detail, 'No se pudo cargar la configuración del scanner'))
    } finally {
      setLoading(false)
    }
  }

  const setScannerField = (field, value) => {
    setScanner((s) => ({ ...s, [field]: value }))
    setHasUnsavedChanges(true)
  }

  const setConfigField = (field, value) => {
    setScanner((s) => ({ ...s, config: { ...s.config, [field]: value } }))
    setHasUnsavedChanges(true)
  }

  const addAsset = () => {
    setConfigField('assets', [...scanner.config.assets, { symbol: '', source: 'bingx', twelve_type: 'all' }])
  }

  const updateAsset = (idx, key, value) => {
    const assets = scanner.config.assets.map((a, i) => (i === idx ? { ...a, [key]: value } : a))
    setConfigField('assets', assets)
  }

  const removeAsset = (idx) => {
    setConfigField('assets', scanner.config.assets.filter((_, i) => i !== idx))
  }

  const toggleTimeframe = (tf) => {
    const list = scanner.config.timeframes.includes(tf)
      ? scanner.config.timeframes.filter((t) => t !== tf)
      : [...scanner.config.timeframes, tf]
    setConfigField('timeframes', list.sort((a, b) => TIMEFRAMES.indexOf(a) - TIMEFRAMES.indexOf(b)))
  }

  const toggleIndicator = (id) => {
    const exists = scanner.config.indicators.find((i) => i.id === id)
    if (exists) {
      setConfigField(
        'indicators',
        scanner.config.indicators.filter((i) => i.id !== id)
      )
    } else {
      setConfigField('indicators', [
        ...scanner.config.indicators,
        { id, config: INDICATOR_DEFAULTS[id] },
      ])
    }
  }

  const updateIndicatorConfig = (id, key, value) => {
    setConfigField(
      'indicators',
      scanner.config.indicators.map((i) =>
        i.id === id ? { ...i, config: { ...i.config, [key]: value } } : i
      )
    )
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const payload = {
        name: scanner.name,
        is_active: scanner.is_active,
        config: {
          ...scanner.config,
          telegram_chat_id: scanner.config.telegram_chat_id || null,
          telegram_thread_id: scanner.config.telegram_thread_id ? parseInt(scanner.config.telegram_thread_id, 10) : null,
        },
      }
      const { data } = await algorithmicScannerService.updateScanner(scannerId, payload)
      setScanner({ ...data, config: mergeDefaults(data.config) })
      setHasUnsavedChanges(false)
      setRunResult(null)
    } catch (e) {
      const detail = e.response?.data?.detail
      setError(formatError(detail, 'Error al guardar'))
    } finally {
      setSaving(false)
    }
  }

  const handleDiagnose = async () => {
    setError(null)
    if (hasUnsavedChanges) {
      setError('Tienes cambios sin guardar. Guarda el scanner antes de diagnosticar.')
      return
    }
    if (!scanner.is_active) {
      setError('El scanner está inactivo. Actívalo antes de diagnosticar.')
      return
    }
    if (!cfg.assets.length || !cfg.timeframes.length || !cfg.indicators.length) {
      setError('Configura al menos un activo, un timeframe y un indicador antes de diagnosticar.')
      return
    }
    setDiagnosing(true)
    try {
      const { data } = await algorithmicScannerService.diagnoseScan(scannerId)
      setDiagnosis(data)
      setTab(1)
    } catch (e) {
      setError(formatError(e.response?.data?.detail, 'Error al ejecutar el diagnóstico'))
    } finally {
      setDiagnosing(false)
    }
  }

  const toggleAsset = (symbol) => {
    setMcSelectedAssets((prev) =>
      prev.includes(symbol) ? prev.filter((s) => s !== symbol) : [...prev, symbol]
    )
  }

  const toggleTimeframeSelection = (tf) => {
    setMcSelectedTimeframes((prev) =>
      prev.includes(tf) ? prev.filter((t) => t !== tf) : [...prev, tf]
    )
  }

  const openMcResult = (job) => {
    setMcResult(job.result)
    setMcOpen(true)
  }

  const removeMcJob = (resultId) => {
    setMcJobs((prev) => prev.filter((j) => j.resultId !== resultId))
  }

  const handleRunMonteCarlo = async () => {
    setMcError(null)
    setMcResult(null)
    if (hasUnsavedChanges) {
      setMcError('Tienes cambios sin guardar. Guarda el scanner antes de ejecutar Monte Carlo.')
      return
    }
    if (mcSelectedAssets.length === 0) {
      setMcError('Selecciona al menos un activo.')
      return
    }
    if (mcSelectedTimeframes.length === 0) {
      setMcError('Selecciona al menos una temporalidad.')
      return
    }
    setMcLoading(true)

    // Enviamos un único request batch con todos los activos/timeframes seleccionados
    // para evitar tormentas de requests (429) y aprovechar el backend batch nativo.
    const assets = mcSelectedAssets.map((a) => String(a || '').trim()).filter(Boolean)
    const timeframes = mcSelectedTimeframes.map((tf) => String(tf || '').trim()).filter(Boolean)

    if (assets.length === 0 || timeframes.length === 0) {
      setMcError('Selecciona al menos un activo y una temporalidad.')
      setMcLoading(false)
      return
    }

    const lookbackDays = Math.min(Math.max(Number(mcParams.lookback_days) || 180, 30), 730)
    const nSimulations = Math.min(Math.max(Number(mcParams.n_simulations) || 1000, 100), 5000)
    if (nSimulations !== Number(mcParams.n_simulations)) {
      setMcError(`El número de simulaciones se ha ajustado a ${nSimulations} (máximo permitido).`)
    }

    const params = {
      assets,
      timeframes,
      lookback_days: lookbackDays,
      n_simulations: nSimulations,
    }

    try {
      debugLog('[MC] payload:', params)
      const { data } = await algorithmicScannerService.runMonteCarlo(scannerId, params)
      if (!data.result_id) {
        setMcError('No se pudo encolar el análisis.')
        setMcLoading(false)
        return
      }

      setMcJobs((prev) => [
        ...prev,
        {
          resultId: data.result_id,
          label: `${assets.length} activo${assets.length > 1 ? 's' : ''} × ${timeframes.length} timeframe${timeframes.length > 1 ? 's' : ''}`,
          status: 'queued',
          createdAt: new Date().toISOString(),
          result: null,
          error: null,
        },
      ])
      // Cerramos el modal para que el usuario siga trabajando
      setMcOpen(false)
      setMcLoading(false)
    } catch (e) {
      setMcError(formatError(e.response?.data?.detail, 'Error al lanzar Monte Carlo'))
      setMcLoading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    )
  }

  if (!scanner) {
    return (
      <div className="card text-center py-12">
        <p className="text-slate-600 dark:text-gray-300">No se pudo cargar el escáner.</p>
        <button onClick={loadConfig} className="btn-primary mt-4">Reintentar</button>
      </div>
    )
  }

  const cfg = scanner.config
  const selectedIndicatorIds = new Set(cfg.indicators.map((i) => i.id))

  return (
    <div className="space-y-6 pb-24 md:pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Link to="/scanner-algorithmic" className="p-2 rounded-lg bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-400 hover:bg-slate-200 dark:hover:bg-gray-700 transition-colors">
            <ArrowLeft size={22} />
          </Link>
          <div className="p-2 rounded-lg bg-blue-600/10 text-blue-600 dark:text-blue-400">
            <Radar size={22} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900 dark:text-white">{scanner?.name || 'Escáner'}</h1>
            <p className="text-xs text-slate-500 dark:text-gray-400">Multi-activo · multi-timeframe · alertas Telegram</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${scanner.is_active ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400' : 'bg-slate-200 dark:bg-gray-800 text-slate-600 dark:text-gray-400'}`}>
            {scanner.is_active ? 'Activo' : 'Inactivo'}
          </span>
          {hasUnsavedChanges && (
            <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400">
              Sin guardar
            </span>
          )}
          <button
            onClick={() => setMcOpen(true)}
            className="btn-secondary flex items-center gap-2"
            title="Análisis Monte Carlo por activo/timeframe"
          >
            <BarChart3 size={16} />
            Monte Carlo
          </button>
          <button
            onClick={handleDiagnose}
            disabled={diagnosing || !scanner.is_active}
            className="btn-secondary flex items-center gap-2"
            title="Ver qué está buscando el escáner sin persistir señales"
          >
            {diagnosing ? <Loader2 size={16} className="animate-spin" /> : <Activity size={16} />}
            Diagnóstico
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="btn-secondary flex items-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            Guardar
          </button>
        </div>
      </div>

      {error && (
        <div className="card border-red-500/30 bg-red-500/10 text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      {/* Monte Carlo jobs en segundo plano */}
      {mcJobs.length > 0 && (
        <div className="card space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-slate-800 dark:text-white flex items-center gap-2">
              <BarChart3 size={16} /> Análisis Monte Carlo
            </h3>
            <button
              onClick={() => window.location.reload()}
              className="text-[10px] text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
              title="Forzar refresco completo de la página"
            >
              <RefreshCw size={12} /> Force Refresh
            </button>
            <button
              onClick={() => {
                if (confirm('¿Borrar todo el historial de análisis Monte Carlo de este scanner?')) {
                  setMcJobs([])
                  localStorage.removeItem(MC_JOBS_KEY)
                }
              }}
              className="text-[10px] text-red-400 hover:text-red-300 flex items-center gap-1"
              title="Borrar todo el historial de jobs"
            >
              <Trash2 size={12} /> Limpiar historial
            </button>
          </div>
          <div className="space-y-2">
            {[...mcJobs].reverse().map((job) => (
              <div
                key={job.resultId}
                className="flex items-center justify-between p-3 rounded-lg bg-slate-50 dark:bg-gray-800/50 border border-slate-200 dark:border-gray-700"
              >
                <div className="flex items-center gap-3">
                  {job.status === 'queued' && <Loader2 size={16} className="animate-spin text-amber-500" />}
                  {job.status === 'running' && <Loader2 size={16} className="animate-spin text-cyan-500" />}
                  {job.status === 'completed' && <span className="text-emerald-500">✓</span>}
                  {job.status === 'failed' && <span className="text-red-500">✕</span>}
                  <div>
                    <p className="text-sm font-medium text-slate-800 dark:text-gray-200">{job.label}</p>
                    <p className="text-[10px] text-slate-500 dark:text-gray-400">
                      ID {job.resultId.slice(0, 8)} · {new Date(job.createdAt).toLocaleTimeString()}
                      <br />
                      {job.status === 'queued' && 'En cola'}
                      {job.status === 'running' && 'Procesando... (puede tardar varios minutos)'}
                      {job.status === 'completed' && (() => {
                        const trades = job.result?.summary?.total_trades ?? job.result?.trades_count ?? 0
                        return `Completado · ${trades} trades · ${new Date(job.finishedAt).toLocaleTimeString()}`
                      })()}
                      {job.status === 'failed' && (job.error || 'Error')}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {job.status === 'completed' && (
                    <button
                      onClick={() => openMcResult(job)}
                      className="btn-secondary text-xs flex items-center gap-1"
                    >
                      Ver resultado
                    </button>
                  )}
                  <button
                    onClick={() => removeMcJob(job.resultId)}
                    className="p-1.5 text-slate-400 hover:text-red-500"
                    title="Descartar"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-0 overflow-x-auto border-b border-slate-200 dark:border-gray-800">
        {['Configuración', 'Diagnóstico', 'Rentabilidad'].map((t, i) => (
          <button
            key={t}
            onClick={() => setTab(i)}
            className={`px-4 py-2 text-sm whitespace-nowrap border-b-2 transition-colors ${
              tab === i
                ? 'border-blue-500 text-slate-900 dark:text-white'
                : 'border-transparent text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-300'
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 0 && (
        <div className="space-y-6">
          {/* General */}
          <Section title="General">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Nombre">
                <input
                  type="text"
                  value={scanner.name}
                  onChange={(e) => setScannerField('name', e.target.value)}
                  className="input"
                />
              </Field>
              <div className="flex items-end pb-2">
                <ToggleField
                  label="Scanner activo"
                  value={scanner.is_active}
                  onChange={(v) => setScannerField('is_active', v)}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <ToggleField
                label="Enviar alertas Telegram"
                value={cfg.send_alert}
                onChange={(v) => setConfigField('send_alert', v)}
              />
              <ToggleField
                label="Filtro EMA50"
                value={cfg.ema50_filter}
                onChange={(v) => setConfigField('ema50_filter', v)}
              />
            </div>
          </Section>

          {/* Assets */}
          <Section title="Activos">
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500 dark:text-gray-400">
                {hasUnsavedChanges ? 'Hay cambios sin guardar. Guarda antes de escanear o ejecutar Monte Carlo.' : 'Los activos se guardan al pulsar Guardar.'}
              </p>
              <button
                onClick={handleSave}
                disabled={saving || !hasUnsavedChanges}
                className="btn-secondary text-xs flex items-center gap-1.5 disabled:opacity-50"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Guardar activos
              </button>
            </div>
            <div className="space-y-3">
              {cfg.assets.length === 0 && (
                <p className="text-sm text-slate-500 dark:text-gray-400">Aún no hay activos configurados.</p>
              )}
              {cfg.assets.map((asset, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row gap-3">
                  <select
                    value={asset.source}
                    onChange={(e) => updateAsset(idx, 'source', e.target.value)}
                    className="input w-full sm:w-36 shrink-0"
                  >
                    {SOURCES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                  {asset.source === 'twelve' && (
                    <select
                      value={asset.twelve_type || 'all'}
                      onChange={(e) => updateAsset(idx, 'twelve_type', e.target.value)}
                      className="input w-full sm:w-40 shrink-0"
                    >
                      {TWELVE_TYPES.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  )}
                  <AssetSymbolInput
                    value={asset.symbol}
                    source={asset.source}
                    twelveType={asset.twelve_type}
                    onChange={(v) => updateAsset(idx, 'symbol', v)}
                    disabled={false}
                  />
                  <button
                    onClick={() => removeAsset(idx)}
                    className="p-2 text-slate-400 hover:text-red-500 transition-colors self-start"
                    title="Eliminar"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
              <button onClick={addAsset} className="btn-ghost text-sm">+ Añadir activo</button>
            </div>
          </Section>

          {/* Timeframes */}
          <Section title="Timeframes">
            <div className="flex flex-wrap gap-2">
              {TIMEFRAMES.map((tf) => (
                <button
                  key={tf}
                  onClick={() => toggleTimeframe(tf)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                    cfg.timeframes.includes(tf)
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'bg-white dark:bg-gray-900 border-slate-300 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-100 dark:hover:bg-gray-800'
                  }`}
                >
                  {tf}
                </button>
              ))}
            </div>
          </Section>

          {/* Indicators */}
          <Section title="Indicadores">
            <div className="space-y-4">
              {Object.entries(INDICATOR_META).map(([id, meta]) => {
                const enabled = selectedIndicatorIds.has(id)
                const indConfig = cfg.indicators.find((i) => i.id === id)?.config || INDICATOR_DEFAULTS[id]
                return (
                  <div key={id} className={`rounded-lg border p-4 transition-colors ${enabled ? 'border-blue-500/30 bg-blue-500/5' : 'border-slate-200 dark:border-gray-800 bg-white dark:bg-gray-900'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-sm text-slate-800 dark:text-gray-200">{meta.label}</span>
                      <ToggleField
                        label=""
                        value={enabled}
                        onChange={() => toggleIndicator(id)}
                      />
                    </div>
                    {enabled && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {meta.fields.map((field) => (
                          <Field key={field.key} label={field.label}>
                            {field.type === 'select' ? (
                              <select
                                value={indConfig[field.key]}
                                onChange={(e) => updateIndicatorConfig(id, field.key, e.target.value)}
                                className="input"
                              >
                                {field.options.map((opt) => (
                                  <option key={opt} value={opt}>{opt}</option>
                                ))}
                              </select>
                            ) : field.type === 'bool' ? (
                              <ToggleField
                                label=""
                                value={!!indConfig[field.key]}
                                onChange={(v) => updateIndicatorConfig(id, field.key, v)}
                              />
                            ) : field.type === 'text' ? (
                              <input
                                type="text"
                                value={indConfig[field.key] || ''}
                                onChange={(e) => updateIndicatorConfig(id, field.key, e.target.value)}
                                className="input"
                              />
                            ) : (
                              <NumberInput
                                value={indConfig[field.key]}
                                onChange={(v) => updateIndicatorConfig(id, field.key, v)}
                                min={field.min}
                                max={field.max}
                                step={field.step}
                              />
                            )}
                          </Field>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </Section>

        </div>
      )}

      {tab === 1 && (
        <div className="space-y-4">
          <div className="card space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="section-title mb-0">Diagnóstico del escáner</h3>
              {diagnosis && (
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-slate-500 dark:text-gray-400">Pares: <span className="font-medium text-slate-700 dark:text-gray-200">{diagnosis.scanned}</span></span>
                  <span className="text-slate-500 dark:text-gray-400">Válidas: <span className="font-medium text-emerald-600 dark:text-emerald-400">{diagnosis.signals}</span></span>
                </div>
              )}
            </div>
            {!diagnosis ? (
              <p className="text-sm text-slate-500 dark:text-gray-400">Pulsa <span className="font-medium">Diagnóstico</span> para ver qué está buscando el escáner en cada activo/timeframe/indicador.</p>
            ) : (
              <div className="overflow-x-auto -mx-4 px-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-gray-800 text-left text-slate-500 dark:text-gray-400">
                      <th className="py-2 pr-3 font-medium">Activo</th>
                      <th className="py-2 pr-3 font-medium">TF</th>
                      <th className="py-2 pr-3 font-medium">Indicador</th>
                      <th className="py-2 pr-3 font-medium">Velas</th>
                      <th className="py-2 pr-3 font-medium">Señal</th>
                      <th className="py-2 pr-3 font-medium">EMA50</th>
                      <th className="py-2 pr-3 font-medium">Dup</th>
                      <th className="py-2 pr-3 font-medium">Motivo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-gray-800">
                    {diagnosis.details.map((row, idx) => (
                      <tr key={idx} className={row.would_persist ? 'bg-emerald-500/5' : ''}>
                        <td className="py-2 pr-3 font-mono text-slate-700 dark:text-gray-300">{row.symbol}</td>
                        <td className="py-2 pr-3 text-slate-600 dark:text-gray-400">{row.timeframe}</td>
                        <td className="py-2 pr-3 text-slate-600 dark:text-gray-400">{row.indicator_id}</td>
                        <td className="py-2 pr-3 text-slate-500 dark:text-gray-500">{row.candles_count}</td>
                        <td className="py-2 pr-3">
                          {row.signal === 'long' && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 font-medium">LONG {row.grade}</span>}
                          {row.signal === 'short' && <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 dark:text-red-400 font-medium">SHORT {row.grade}</span>}
                          {row.signal === 'none' && <span className="text-slate-400 dark:text-gray-500">—</span>}
                        </td>
                        <td className="py-2 pr-3">
                          {row.ema50_allowed === true && <span className="text-emerald-600 dark:text-emerald-400">✓</span>}
                          {row.ema50_allowed === false && <span className="text-red-500" title="Filtrado por EMA50">✕</span>}
                          {row.ema50_allowed === null && <span className="text-slate-300 dark:text-gray-600">—</span>}
                        </td>
                        <td className="py-2 pr-3">
                          {row.is_duplicate === true && <span className="text-amber-500" title="Ya alertada">dup</span>}
                          {row.is_duplicate === false && <span className="text-emerald-600 dark:text-emerald-400">nueva</span>}
                          {row.is_duplicate === null && <span className="text-slate-300 dark:text-gray-600">—</span>}
                        </td>
                        <td className="py-2 pr-3 text-slate-500 dark:text-gray-400">{row.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="text-xs text-slate-500 dark:text-gray-400">
            El diagnóstico ejecuta el escáner <span className="font-medium">sin persistir señales ni enviar alertas</span>. Úsalo para verificar que la configuración funciona.
          </div>
        </div>
      )}

      {tab === 2 && <SignalPerformanceDashboard scannerId={scannerId} />}

      {mcOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
          <div className="bg-slate-950 rounded-xl shadow-2xl w-full max-w-6xl max-h-[95vh] overflow-y-auto border border-slate-800">
            <div className="flex items-center justify-between p-4 border-b border-slate-800 sticky top-0 bg-slate-950 z-10">
              <h3 className="font-semibold text-white">Monte Carlo por activo</h3>
              <button onClick={() => { setMcOpen(false); setMcResult(null); setMcError(null) }} className="text-slate-400 hover:text-slate-200">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-6">
              <p className="text-sm text-slate-400">
                Simula trades históricos con los indicadores de este scanner y ejecuta validación Monte Carlo.
                Puedes cerrar esta ventana; el análisis continuará en segundo plano.
              </p>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-xs font-semibold text-slate-300 uppercase">Activos</label>
                    <button
                      onClick={() => setMcSelectedAssets(mcSelectedAssets.length === cfg.assets.length ? [] : cfg.assets.map((a) => a.symbol))}
                      className="text-[10px] text-cyan-400 hover:text-cyan-300"
                    >
                      {mcSelectedAssets.length === cfg.assets.length ? 'Ninguno' : 'Todos'}
                    </button>
                  </div>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {cfg.assets.map((a) => (
                      <label key={a.symbol} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={mcSelectedAssets.includes(a.symbol)}
                          onChange={() => toggleAsset(a.symbol)}
                          className="accent-cyan-500"
                        />
                        {a.symbol}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-4">
                  <div className="flex items-center justify-between mb-3">
                    <label className="text-xs font-semibold text-slate-300 uppercase">Temporalidades</label>
                    <button
                      onClick={() => setMcSelectedTimeframes(mcSelectedTimeframes.length === cfg.timeframes.length ? [] : [...cfg.timeframes])}
                      className="text-[10px] text-cyan-400 hover:text-cyan-300"
                    >
                      {mcSelectedTimeframes.length === cfg.timeframes.length ? 'Ninguna' : 'Todas'}
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {cfg.timeframes.map((tf) => (
                      <label key={tf} className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={mcSelectedTimeframes.includes(tf)}
                          onChange={() => toggleTimeframeSelection(tf)}
                          className="accent-cyan-500"
                        />
                        {tf}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5">Días históricos</label>
                    <input
                      type="number"
                      min={30}
                      max={730}
                      value={mcParams.lookback_days}
                      onChange={(e) => setMcParams((p) => ({ ...p, lookback_days: parseInt(e.target.value, 10) }))}
                      className="input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-slate-400 mb-1.5">Simulaciones</label>
                    <input
                      type="number"
                      min={100}
                      max={5000}
                      step={100}
                      value={mcParams.n_simulations}
                      onChange={(e) => setMcParams((p) => ({ ...p, n_simulations: parseInt(e.target.value, 10) }))}
                      className="input w-full"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  onClick={handleRunMonteCarlo}
                  disabled={mcLoading}
                  className="btn-primary flex items-center gap-2"
                >
                  {mcLoading ? <Loader2 size={16} className="animate-spin" /> : <BarChart3 size={16} />}
                  {mcLoading ? 'Encolando...' : 'Ejecutar Monte Carlo'}
                </button>
              </div>

              {mcError && (
                <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg p-3">{mcError}</div>
              )}

              {mcResult && (
                <ScannerMonteCarloDashboard result={mcResult} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function MetricCard({ label, value }) {
  return (
    <div className="bg-slate-50 dark:bg-gray-800 rounded-lg p-3">
      <p className="text-[10px] text-slate-500 dark:text-gray-400 uppercase">{label}</p>
      <p className="text-lg font-semibold text-slate-800 dark:text-gray-200">{value ?? '—'}</p>
    </div>
  )
}
